import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  adminSafetySummary,
  adminListAlerts,
  adminResolveAlert,
  adminListReports,
  adminUpdateReportStatus,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import AdminApplications from '../components/AdminApplications';
import {
  ShieldAlert,
  AlertTriangle,
  Users,
  CheckCircle2,
  Phone,
  Mail,
  MapPin,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatCard = ({ label, value, tone = 'gold', testid }) => {
  const toneCls = {
    gold: 'text-[#C0A062]',
    danger: 'text-red-400',
    success: 'text-emerald-400',
    muted: 'text-zinc-300',
  }[tone];
  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-5" data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">{label}</p>
      <p className={`text-3xl font-bold ${toneCls}`}>{value}</p>
    </div>
  );
};

const AlertCard = ({ alert, onResolve }) => {
  const isActive = alert.status !== 'resolved';
  const u = alert.user || {};
  const loc = alert.location || {};
  const mapsUrl = loc.lat && loc.lng ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}` : null;
  return (
    <div
      className={`rounded-lg border p-5 ${
        isActive ? 'border-red-500/40 bg-red-500/5' : 'border-zinc-800 bg-[#18181B] opacity-70'
      }`}
      data-testid={`admin-alert-${alert.id}`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
            isActive ? 'bg-red-500/15 border border-red-500/40' : 'bg-zinc-800 border border-zinc-700'
          }`}>
            <ShieldAlert className={`w-5 h-5 ${isActive ? 'text-red-400' : 'text-zinc-500'}`} />
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">{u.full_name || 'Unknown user'}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {alert.alert_type || 'SOS'} · {formatWhen(alert.created_at)}
            </p>
          </div>
        </div>
        <Badge className={isActive ? 'bg-red-500/20 text-red-300 border-red-500/30' : 'bg-zinc-700 text-zinc-300'}>
          {alert.status || 'active'}
        </Badge>
      </div>

      <div className="grid sm:grid-cols-2 gap-2 text-xs text-zinc-300 mb-3">
        {u.phone && (
          <a href={`tel:${u.phone}`} className="flex items-center gap-1.5 hover:text-white">
            <Phone className="w-3.5 h-3.5 text-[#C0A062]" />
            {u.phone}
          </a>
        )}
        {u.email && (
          <a href={`mailto:${u.email}`} className="flex items-center gap-1.5 hover:text-white break-all">
            <Mail className="w-3.5 h-3.5 text-[#C0A062]" />
            {u.email}
          </a>
        )}
        {(loc.address || u.city) && (
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5 text-[#C0A062]" />
            <span className="truncate">{loc.address || u.city}</span>
          </div>
        )}
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-white text-[#C0A062]">
            <ExternalLink className="w-3.5 h-3.5" />
            Open in Maps
          </a>
        )}
      </div>

      {alert.message && (
        <p className="text-sm text-zinc-200 bg-zinc-900/60 border border-zinc-800 rounded-md p-3 mb-3 whitespace-pre-line">
          {alert.message}
        </p>
      )}

      {isActive && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => onResolve(alert.id)}
            className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"
            data-testid={`admin-resolve-alert-${alert.id}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            Mark resolved
          </Button>
        </div>
      )}
    </div>
  );
};

const ReportCard = ({ report, onSetStatus }) => {
  const r = report.reporter || {};
  const t = report.trainer || {};
  const statusToneCls = {
    under_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    resolved: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    dismissed: 'bg-zinc-700 text-zinc-300 border-zinc-600',
  }[report.status] || 'bg-zinc-700 text-zinc-300';

  return (
    <div className="rounded-lg border border-zinc-800 bg-[#18181B] p-5" data-testid={`admin-report-${report.id}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold truncate">
              {report.concern_type || 'Concern'}
              {t.full_name && <span className="text-zinc-400 font-normal"> · about {t.full_name}</span>}
            </p>
            <p className="text-xs text-zinc-500 mt-0.5">
              Reported by {r.full_name || report.reporter_name || 'Unknown'} ({r.role || ', '}) · {formatWhen(report.created_at)}
            </p>
          </div>
        </div>
        <Badge className={statusToneCls}>{(report.status || 'under_review').replace('_', ' ')}</Badge>
      </div>

      {report.description && (
        <p className="text-sm text-zinc-200 bg-zinc-900/60 border border-zinc-800 rounded-md p-3 mb-3 whitespace-pre-line">
          {report.description}
        </p>
      )}

      {report.status === 'under_review' && (
        <div className="flex flex-wrap justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onSetStatus(report.id, 'dismissed')}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid={`admin-dismiss-report-${report.id}`}
          >
            Dismiss
          </Button>
          <Button
            size="sm"
            onClick={() => onSetStatus(report.id, 'resolved')}
            className="bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25"
            data-testid={`admin-resolve-report-${report.id}`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1.5" />
            Mark resolved
          </Button>
        </div>
      )}
    </div>
  );
};

const AdminSafety = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, isAdmin } = useAuth();

  const [summary, setSummary] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [reports, setReports] = useState([]);
  const [alertFilter, setAlertFilter] = useState('active'); // 'active' | 'resolved' | 'all'
  const [reportFilter, setReportFilter] = useState('under_review');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, alertsRes, reportsRes] = await Promise.all([
        adminSafetySummary(),
        adminListAlerts(alertFilter === 'all' ? undefined : alertFilter),
        adminListReports(reportFilter === 'all' ? undefined : reportFilter),
      ]);
      setSummary(sumRes.data);
      setAlerts(alertsRes.data || []);
      setReports(reportsRes.data || []);
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.detail || 'Could not load admin data';
      toast.error(typeof msg === 'string' ? msg : 'Could not load admin data');
    } finally {
      setLoading(false);
    }
  }, [alertFilter, reportFilter]);

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      // not an admin, bounce out
      return;
    }
    if (!authLoading && isAdmin) refresh();
  }, [authLoading, user, isAdmin, refresh]);

  const handleResolveAlert = async (id) => {
    try {
      await adminResolveAlert(id);
      toast.success('Alert resolved');
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resolve alert');
    }
  };

  const handleSetReportStatus = async (id, status) => {
    try {
      await adminUpdateReportStatus(id, status);
      toast.success(`Report ${status.replace('_', ' ')}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update report');
    }
  };

  const headerStats = useMemo(() => {
    if (!summary) return null;
    return [
      { label: 'Active SOS', value: summary.alerts.active, tone: summary.alerts.active > 0 ? 'danger' : 'muted', testid: 'stat-active-sos' },
      { label: 'Reports under review', value: summary.reports.under_review, tone: summary.reports.under_review > 0 ? 'gold' : 'muted', testid: 'stat-reports-under-review' },
      { label: 'Waitlist signups', value: summary.platform.waitlist || 0, tone: 'gold', testid: 'stat-waitlist' },
      { label: 'Active trainers', value: summary.platform.trainers, tone: 'muted', testid: 'stat-active-trainers' },
    ];
  }, [summary]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#09090B]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-[#18181B] border border-zinc-800 rounded-lg p-8 text-center" data-testid="admin-forbidden">
          <ShieldAlert className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Admin access required</h1>
          <p className="text-sm text-zinc-400 mb-6">
            This page is restricted to DojoDash Trust &amp; Safety staff. If you should have access, ask
            the platform owner to add your email to the <code className="text-zinc-300">ADMIN_EMAILS</code> environment variable.
          </p>
          <Button onClick={() => navigate('/')} className="bg-[#C0A062] text-black hover:bg-[#a98a52]">
            Back home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-safety-page">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C0A062] font-semibold mb-2">Trust &amp; Safety</p>
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <ShieldAlert className="w-7 h-7 text-red-400" />
              Admin dashboard
            </h1>
            <p className="text-sm text-zinc-400 mt-1">SOS alerts and incident reports across the platform.</p>
          </div>
          <Button
            variant="outline"
            onClick={refresh}
            disabled={loading}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            data-testid="admin-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Refresh
          </Button>
        </header>

        {/* Summary cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {headerStats
            ? headerStats.map((s) => <StatCard key={s.label} {...s} />)
            : Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="bg-[#18181B] border border-zinc-800 rounded-lg p-5 h-[92px] animate-pulse" />
              ))}
        </section>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* SOS alerts column */}
          <section data-testid="admin-alerts-section">
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-400" />
                SOS Alerts
              </h2>
              <div className="inline-flex rounded-md border border-zinc-800 bg-[#18181B] p-0.5 text-xs">
                {['active', 'resolved', 'all'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setAlertFilter(f)}
                    className={`px-3 py-1 rounded ${alertFilter === f ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}
                    data-testid={`admin-alert-filter-${f}`}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>
            {loading ? (
              <div className="text-zinc-500 text-sm py-8 text-center">Loading…</div>
            ) : alerts.length === 0 ? (
              <div className="text-zinc-500 text-sm py-12 text-center bg-[#18181B] border border-zinc-800 rounded-lg" data-testid="admin-alerts-empty">
                <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                No {alertFilter === 'all' ? '' : alertFilter} alerts.
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-alerts-list">
                {alerts.map((a) => (
                  <AlertCard key={a.id} alert={a} onResolve={handleResolveAlert} />
                ))}
              </div>
            )}
          </section>

          {/* Incident reports column */}
          <section data-testid="admin-reports-section">
            <div className="flex items-end justify-between mb-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Incident Reports
              </h2>
              <div className="inline-flex rounded-md border border-zinc-800 bg-[#18181B] p-0.5 text-xs">
                {['under_review', 'resolved', 'all'].map((f) => (
                  <button
                    key={f}
                    onClick={() => setReportFilter(f)}
                    className={`px-3 py-1 rounded ${reportFilter === f ? 'bg-zinc-700 text-white' : 'text-zinc-400 hover:text-white'}`}
                    data-testid={`admin-report-filter-${f}`}
                  >
                    {f.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            {loading ? (
              <div className="text-zinc-500 text-sm py-8 text-center">Loading…</div>
            ) : reports.length === 0 ? (
              <div className="text-zinc-500 text-sm py-12 text-center bg-[#18181B] border border-zinc-800 rounded-lg" data-testid="admin-reports-empty">
                <Users className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                No {reportFilter === 'all' ? '' : reportFilter.replace('_', ' ')} reports.
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-reports-list">
                {reports.map((r) => (
                  <ReportCard key={r.id} report={r} onSetStatus={handleSetReportStatus} />
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Trainer applications */}
        <section className="mt-12">
          <AdminApplications />
        </section>
      </div>
    </div>
  );
};

export default AdminSafety;
